import React, { useEffect, useMemo, useRef, useState } from 'react';
import { FaMinus, FaPlus } from 'react-icons/fa';

interface ImageViewerProps {
    src: string;
    alt?: string;
}

interface DragState {
    pointerId: number;
    startX: number;
    startY: number;
    imageX: number;
    imageY: number;
}

interface Size {
    width: number;
    height: number;
}

interface Position {
    x: number;
    y: number;
}

const MIN_SCALE = 0.1;
const MAX_SCALE = 4;

const ImageViewer: React.FC<ImageViewerProps> = ({ src, alt = 'Enlarged image' }) => {
    const stageRef = useRef<HTMLDivElement | null>(null);
    const dragStateRef = useRef<DragState | null>(null);
    const [stageSize, setStageSize] = useState<Size | null>(null);
    const [imageSize, setImageSize] = useState<Size | null>(null);
    const [scale, setScale] = useState(1);
    const [fitScale, setFitScale] = useState(1);
    const [hasCustomZoom, setHasCustomZoom] = useState(false);
    const [position, setPosition] = useState<Position>({ x: 0, y: 0 });
    const [isDragging, setIsDragging] = useState(false);

    const scaledSize = useMemo(() => {
        if (!imageSize) return null;

        return {
            width: imageSize.width * scale,
            height: imageSize.height * scale
        };
    }, [imageSize, scale]);

    const updateStageSize = () => {
        const stage = stageRef.current;
        if (!stage) return;

        setStageSize({
            width: stage.clientWidth,
            height: stage.clientHeight
        });
    };

    const getCenteredPosition = (nextScale: number): Position => {
        if (!imageSize || !stageSize) return { x: 0, y: 0 };

        const width = imageSize.width * nextScale;
        const height = imageSize.height * nextScale;

        return {
            x: (stageSize.width - width) / 2,
            y: (stageSize.height - height) / 2
        };
    };

    const clampPosition = (nextPosition: Position, nextScale: number): Position => {
        if (!imageSize || !stageSize) return nextPosition;

        const width = imageSize.width * nextScale;
        const height = imageSize.height * nextScale;

        const minX = Math.min(0, stageSize.width - width);
        const maxX = width <= stageSize.width ? (stageSize.width - width) / 2 : 0;
        const minY = Math.min(0, stageSize.height - height);
        const maxY = height <= stageSize.height ? (stageSize.height - height) / 2 : 0;

        return {
            x: Math.min(maxX, Math.max(minX, nextPosition.x)),
            y: Math.min(maxY, Math.max(minY, nextPosition.y))
        };
    };

    const applyScale = (nextScale: number, customZoom: boolean) => {
        if (!imageSize || !stageSize) return;

        const boundedScale = Math.min(MAX_SCALE, Math.max(MIN_SCALE, Number(nextScale.toFixed(2))));
        const focalPoint = {
            x: stageSize.width / 2,
            y: stageSize.height / 2
        };

        const imageCoordinate = {
            x: (focalPoint.x - position.x) / scale,
            y: (focalPoint.y - position.y) / scale
        };

        const nextPosition = clampPosition({
            x: focalPoint.x - imageCoordinate.x * boundedScale,
            y: focalPoint.y - imageCoordinate.y * boundedScale
        }, boundedScale);

        setScale(boundedScale);
        setHasCustomZoom(customZoom);
        setPosition(nextPosition);
    };

    useEffect(() => {
        setImageSize(null);
        setStageSize(null);
        setScale(1);
        setFitScale(1);
        setHasCustomZoom(false);
        setPosition({ x: 0, y: 0 });
        setIsDragging(false);
        dragStateRef.current = null;
    }, [src]);

    useEffect(() => {
        updateStageSize();

        const stage = stageRef.current;
        if (!stage) return;

        const observer = new ResizeObserver(() => updateStageSize());
        observer.observe(stage);

        window.addEventListener('resize', updateStageSize);
        return () => {
            observer.disconnect();
            window.removeEventListener('resize', updateStageSize);
        };
    }, []);

    useEffect(() => {
        if (!imageSize || !stageSize) return;

        const horizontalPadding = stageSize.width >= 640 ? 80 : 48;
        const verticalPadding = stageSize.width >= 640 ? 80 : 48;
        const availableWidth = Math.max(1, stageSize.width - horizontalPadding);
        const availableHeight = Math.max(1, stageSize.height - verticalPadding);
        const nextFitScale = Math.min(availableWidth / imageSize.width, availableHeight / imageSize.height, 1);

        setFitScale(nextFitScale);

        if (!hasCustomZoom) {
            setScale(nextFitScale);
            setPosition(clampPosition(getCenteredPosition(nextFitScale), nextFitScale));
            return;
        }

        setPosition((currentPosition) => clampPosition(currentPosition, scale));
    }, [hasCustomZoom, imageSize, scale, stageSize]);

    const handlePointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
        if (!scaledSize || !stageSize) return;

        const canPan = scaledSize.width > stageSize.width || scaledSize.height > stageSize.height;
        if (!canPan) return;

        dragStateRef.current = {
            pointerId: event.pointerId,
            startX: event.clientX,
            startY: event.clientY,
            imageX: position.x,
            imageY: position.y
        };

        setIsDragging(true);
        event.currentTarget.setPointerCapture(event.pointerId);
        event.preventDefault();
    };

    const handlePointerMove = (event: React.PointerEvent<HTMLDivElement>) => {
        const dragState = dragStateRef.current;
        if (!dragState || dragState.pointerId !== event.pointerId) return;

        setPosition(clampPosition({
            x: dragState.imageX + (event.clientX - dragState.startX),
            y: dragState.imageY + (event.clientY - dragState.startY)
        }, scale));

        event.preventDefault();
    };

    const handlePointerEnd = (event: React.PointerEvent<HTMLDivElement>) => {
        const dragState = dragStateRef.current;
        if (!dragState || dragState.pointerId !== event.pointerId) return;

        dragStateRef.current = null;
        setIsDragging(false);

        if (event.currentTarget.hasPointerCapture(event.pointerId)) {
            event.currentTarget.releasePointerCapture(event.pointerId);
        }
    };

    const handleZoomOut = () => applyScale(scale - 0.1, true);
    const handleZoomIn = () => applyScale(scale + 0.1, true);

    const handleFitToScreen = () => {
        setHasCustomZoom(false);
        setScale(fitScale);
        setPosition(clampPosition(getCenteredPosition(fitScale), fitScale));
    };

    const canPan = !!(scaledSize && stageSize && (scaledSize.width > stageSize.width || scaledSize.height > stageSize.height));
    const zoomPercent = Math.round(scale * 100);

    return (
        <div className="relative h-full w-full p-3 sm:p-4">
            <div className="absolute left-6 top-6 z-10 flex items-center gap-2 rounded-full border border-white/10 bg-neutral-950/80 px-2 py-1.5 text-white shadow-lg backdrop-blur-sm">
                <button
                    className="rounded-full p-2 transition-colors hover:bg-white/10 disabled:cursor-not-allowed disabled:opacity-40"
                    disabled={scale <= MIN_SCALE}
                    onClick={handleZoomOut}
                    aria-label="Zoom out"
                    title="Zoom out"
                    type="button"
                >
                    <FaMinus className="h-3 w-3" />
                </button>
                <button
                    className="min-w-14 rounded-full px-2 py-1 text-xs font-medium tracking-wide text-white/80 transition-colors hover:bg-white/10"
                    onClick={handleFitToScreen}
                    title="Fit to screen"
                    type="button"
                >
                    {zoomPercent}%
                </button>
                <button
                    className="rounded-full p-2 transition-colors hover:bg-white/10 disabled:cursor-not-allowed disabled:opacity-40"
                    disabled={scale >= MAX_SCALE}
                    onClick={handleZoomIn}
                    aria-label="Zoom in"
                    title="Zoom in"
                    type="button"
                >
                    <FaPlus className="h-3 w-3" />
                </button>
            </div>
            <div
                ref={stageRef}
                className={`relative h-full w-full overflow-hidden rounded-[1.25rem] ${canPan ? (isDragging ? 'cursor-grabbing' : 'cursor-grab') : 'cursor-default'}`}
                onPointerDown={handlePointerDown}
                onPointerMove={handlePointerMove}
                onPointerUp={handlePointerEnd}
                onPointerCancel={handlePointerEnd}
                style={{ touchAction: canPan ? 'none' : 'auto' }}
            >
                <img
                    src={src}
                    alt={alt}
                    className="absolute block max-w-none select-none"
                    draggable={false}
                    onDragStart={(event) => event.preventDefault()}
                    onLoad={(event) => {
                        const target = event.currentTarget;
                        setImageSize({
                            width: target.naturalWidth,
                            height: target.naturalHeight
                        });
                    }}
                    style={{
                        width: scaledSize?.width,
                        height: scaledSize?.height,
                        left: position.x,
                        top: position.y
                    }}
                />
            </div>
        </div>
    );
};

export default ImageViewer;
